/**
 * MagazineManual.js
 * Manual subscription management panel (Add Subscriber and Add Guest).
 * Autocompletes user selection and lists all manual entries with edit modal dialog.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import {
  UserPlus, RefreshCw, Loader, Search, Inbox, ChevronLeft, ChevronRight, Edit2, X, Check, Calendar
} from 'lucide-react';

const MagazineManual = ({ toast }) => {
  const [activeSubTab, setActiveSubTab] = useState('subscriber'); // subscriber, guest
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Form input state
  const [form, setForm] = useState({
    user_type: 'registered', // registered, new
    selected_user: null, // { email, name, phone }
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    delivery_address: '',
    pincode: '',
    amount: 699, // default yearly config
    give_1_year: true // for guest checkmark
  });

  // Client-side validation errors state
  const [formErrors, setFormErrors] = useState({
    selected_user: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    delivery_address: '',
    pincode: '',
    amount: ''
  });

  // User autocomplete search input value
  const [userSearchInput, setUserSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showResultsDropdown, setShowResultsDropdown] = useState(false);

  // Table date range filters
  const [tableFilters, setTableFilters] = useState({
    start_date: '',
    end_date: ''
  });

  // Edit Modal State
  const [editItem, setEditItem] = useState(null); // Row object being edited
  const [editForm, setEditForm] = useState({
    delivery_address: '',
    pincode: '',
    amount: 0,
    end_date: '',
    is_lifetime: false
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);

  const autocompleteRef = useRef(null);

  const fetchManualHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await vendorService.getManualSubscriptionsList({
        page,
        page_size: 25,
        start_date: tableFilters.start_date,
        end_date: tableFilters.end_date
      });
      if (res.success && res.data) {
        setHistory(res.data.history || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / 25)));
      } else {
        toast.error(res.error || 'Failed to load manual grants history.');
      }
    } catch {
      toast.error('Failed to load manual grants history.');
    } finally {
      setLoadingHistory(false);
    }
  }, [page, tableFilters, toast]);

  useEffect(() => {
    fetchManualHistory();
  }, [fetchManualHistory, page]);

  // Reset page to 1 when table filters change
  useEffect(() => {
    setPage(1);
  }, [tableFilters]);

  // Click outside to dismiss autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowResultsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle autocomplete user typing
  const handleUserSearchChange = async (val) => {
    setUserSearchInput(val);
    
    // If a user was previously selected, clear it upon edit
    if (form.selected_user) {
      setForm(f => ({ ...f, selected_user: null }));
    }

    if (val.trim().length < 2) {
      setSearchResults([]);
      setShowResultsDropdown(false);
      return;
    }

    setSearchingUsers(true);
    try {
      const res = await vendorService.searchSystemUsers(val);
      if (res.success && res.data) {
        setSearchResults(res.data.users || []);
        setShowResultsDropdown(true);
      }
    } catch {
      // ignore
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleSelectUser = (user) => {
    setForm(prev => ({
      ...prev,
      selected_user: user
    }));
    setUserSearchInput(`${user.name} (${user.email})`);
    setSearchResults([]);
    setShowResultsDropdown(false);
  };

  const handleResetForm = () => {
    setForm({
      user_type: 'registered',
      selected_user: null,
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      delivery_address: '',
      pincode: '',
      amount: activeSubTab === 'subscriber' ? 699 : 0,
      give_1_year: true
    });
    setUserSearchInput('');
    setSearchResults([]);
    setShowResultsDropdown(false);
    setFormErrors({
      selected_user: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      delivery_address: '',
      pincode: '',
      amount: ''
    });
  };

  useEffect(() => {
    handleResetForm();
  }, [activeSubTab]);

  const handleAddManual = async (e) => {
    e.preventDefault();

    // Reset error notes
    const errs = {
      selected_user: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      delivery_address: '',
      pincode: '',
      amount: ''
    };
    let hasErr = false;

    if (form.user_type === 'registered') {
      if (!form.selected_user) {
        errs.selected_user = 'Please search and select a registered system user.';
        hasErr = true;
      }
    } else {
      if (!form.guest_name.trim()) {
        errs.guest_name = 'Full name is required.';
        hasErr = true;
      }
      if (!form.guest_email.trim()) {
        errs.guest_email = 'Email address is required.';
        hasErr = true;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.guest_email.trim())) {
          errs.guest_email = 'Invalid email address format.';
          hasErr = true;
        }
      }
      if (!form.guest_phone.trim()) {
        errs.guest_phone = 'Mobile number is required.';
        hasErr = true;
      } else {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(form.guest_phone.trim())) {
          errs.guest_phone = 'Invalid mobile number format (must be 10 digits).';
          hasErr = true;
        }
      }
    }

    if (!form.delivery_address.trim()) {
      errs.delivery_address = 'Courier delivery address is required.';
      hasErr = true;
    }
    if (!form.pincode.trim()) {
      errs.pincode = 'Pincode is required.';
      hasErr = true;
    } else {
      const pinRegex = /^[1-9][0-9]{5}$/;
      if (!pinRegex.test(form.pincode.trim())) {
        errs.pincode = 'Invalid pincode format (6 digits required, cannot start with 0).';
        hasErr = true;
      }
    }

    if (activeSubTab === 'subscriber') {
      if (form.amount === '' || form.amount === undefined || isNaN(form.amount)) {
        errs.amount = 'Charged price is required.';
        hasErr = true;
      } else if (Number(form.amount) < 0) {
        errs.amount = 'Charged price cannot be negative.';
        hasErr = true;
      }
    }

    if (hasErr) {
      setFormErrors(errs);
      toast.error('Please correct the highlighted form validation errors.');
      return;
    }

    setFormErrors({
      selected_user: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      delivery_address: '',
      pincode: '',
      amount: ''
    });
    setSubmittingAdd(true);
    
    try {
      const payload = {
        delivery_address: form.delivery_address,
        pincode: form.pincode,
        amount: Number(form.amount) || 0,
        is_guest: activeSubTab === 'guest',
        give_1_year: form.give_1_year,
        is_new_user: form.user_type === 'new',
        vendor_email: form.user_type === 'new' ? form.guest_email.trim() : form.selected_user.email,
        guest_name: form.user_type === 'new' ? form.guest_name.trim() : null,
        guest_phone: form.user_type === 'new' ? form.guest_phone.trim() : null
      };

      const res = await vendorService.createManualSubscription(payload);

      if (res.success) {
        const recipientName = form.user_type === 'new' ? form.guest_name : form.selected_user.name;
        toast.success(`Successfully granted subscription to ${recipientName}!`);
        handleResetForm();
        setPage(1);
        fetchManualHistory();
      } else {
        toast.error(res.error || 'Failed to grant manual subscription.');
      }
    } catch (err) {
      console.error('Error creating manual subscription:', err);
      toast.error(err?.message || 'Database connection error occurred. Please try again.');
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleOpenEdit = (item) => {
    setEditItem(item);
    setEditForm({
      delivery_address: item.delivery_address,
      pincode: item.pincode,
      amount: item.amount,
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      is_lifetime: item.end_date === null
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.delivery_address.trim() || !editForm.pincode.trim()) {
      toast.error('Address and Pincode are required.');
      return;
    }

    setSubmittingEdit(true);
    try {
      const formattedDate = editForm.is_lifetime ? null : editForm.end_date ? `${editForm.end_date}T00:00:00` : null;
      const res = await vendorService.updateManualSubscription(editItem.id, {
        delivery_address: editForm.delivery_address,
        pincode: editForm.pincode,
        amount: Number(editForm.amount) || 0,
        end_date: formattedDate,
        is_lifetime: editForm.is_lifetime
      });

      if (res.success) {
        toast.success('Manual subscription entry updated successfully!');
        setEditItem(null);
        fetchManualHistory();
      } else {
        toast.error(res.error || 'Failed to update entry.');
      }
    } catch {
      toast.error('Connection error updating subscription.');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Lifetime (Comped)';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-5 text-left">
        <div>
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <UserPlus size={18} className="text-[#2ab391]" />
            Add Subscriber (Manual Grant)
          </h3>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Grant subscriptions manually to print buyers or guest editors.</p>
        </div>

        {/* Double sub-tabs (Subscriber / Guest) */}
        <div className="flex bg-gray-100/80 p-0.5 rounded-xl self-start md:self-center font-bold text-xs">
          <button
            onClick={() => setActiveSubTab('subscriber')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeSubTab === 'subscriber'
                ? 'bg-white text-gray-800 shadow-xs'
                : 'text-gray-450 hover:text-gray-700'
            }`}
          >
            Add Subscriber
          </button>
          <button
            onClick={() => setActiveSubTab('guest')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeSubTab === 'guest'
                ? 'bg-white text-gray-800 shadow-xs'
                : 'text-gray-450 hover:text-gray-700'
            }`}
          >
            Add Guest
          </button>
        </div>
      </div>

      {/* Grant manual Form */}
      <form onSubmit={handleAddManual} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs text-left space-y-4">
        <div className="flex gap-6 border-b border-gray-150 pb-3 font-semibold text-xs text-gray-600">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="radio"
              name="user_type"
              checked={form.user_type === 'registered'}
              onChange={() => setForm(f => ({ ...f, user_type: 'registered' }))}
              className="text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
            />
            <span>Registered System User</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="radio"
              name="user_type"
              checked={form.user_type === 'new'}
              onChange={() => setForm(f => ({ ...f, user_type: 'new' }))}
              className="text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
            />
            <span>New / Unregistered User</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Conditional inputs depending on user selection scenario */}
          {form.user_type === 'registered' ? (
            <div ref={autocompleteRef} className="relative">
              <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                Select Registered User *
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type registered name or email..."
                  value={userSearchInput}
                  onChange={(e) => handleUserSearchChange(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl pl-4 pr-9 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                />
                {searchingUsers ? (
                  <RefreshCw className="animate-spin text-[#2ab391] w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2" />
                ) : (
                  <Search size={14} className="text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                )}
              </div>

              {/* Dropdown Overlay */}
              {showResultsDropdown && searchResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto z-50 divide-y divide-gray-50">
                  {searchResults.map((usr) => (
                    <button
                      key={usr.email}
                      type="button"
                      onClick={() => handleSelectUser(usr)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50/80 transition-colors text-xs font-semibold block"
                    >
                      <span className="text-gray-800 font-bold block">{usr.name}</span>
                      <span className="text-gray-455 text-[10px]">{usr.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {showResultsDropdown && searchResults.length === 0 && userSearchInput.trim().length >= 2 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl p-3 shadow-lg z-50 text-center text-xs font-semibold text-gray-455">
                  No registered users found matching "{userSearchInput}"
                </div>
              )}
              {formErrors.selected_user && (
                <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.selected_user}</p>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Guest Full Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter guest's full name..."
                  value={form.guest_name}
                  onChange={(e) => {
                    setForm(f => ({ ...f, guest_name: e.target.value }));
                    if (e.target.value.trim()) {
                      setFormErrors(prev => ({ ...prev, guest_name: '' }));
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                />
                {formErrors.guest_name && (
                  <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.guest_name}</p>
                )}
              </div>

              <div>
                <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Guest Email Address *
                </label>
                <input
                  type="email"
                  placeholder="Enter guest's email address..."
                  value={form.guest_email}
                  onChange={(e) => {
                    setForm(f => ({ ...f, guest_email: e.target.value }));
                    if (e.target.value.trim()) {
                      setFormErrors(prev => ({ ...prev, guest_email: '' }));
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                />
                {formErrors.guest_email && (
                  <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.guest_email}</p>
                )}
              </div>

              <div>
                <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Guest Mobile Number *
                </label>
                <input
                  type="text"
                  placeholder="Enter 10-digit mobile number..."
                  value={form.guest_phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) {
                      setForm(f => ({ ...f, guest_phone: val }));
                      if (val.length === 10) {
                        setFormErrors(prev => ({ ...prev, guest_phone: '' }));
                      }
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                />
                {formErrors.guest_phone && (
                  <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.guest_phone}</p>
                )}
              </div>
            </>
          )}

          {/* Delivery Address */}
          <div>
            <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Delivery / Courier Address *
            </label>
            <input
              type="text"
              placeholder="Full dispatch address..."
              value={form.delivery_address}
              onChange={(e) => {
                setForm(f => ({ ...f, delivery_address: e.target.value }));
                if (e.target.value.trim()) {
                  setFormErrors(prev => ({ ...prev, delivery_address: '' }));
                }
              }}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
            />
            {formErrors.delivery_address && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.delivery_address}</p>
            )}
          </div>

          {/* Pincode */}
          <div>
            <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Pincode *
            </label>
            <input
              type="text"
              placeholder="6-digit PIN code..."
              value={form.pincode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                if (val.length <= 6) {
                  setForm(f => ({ ...f, pincode: val }));
                  const pinRegex = /^[1-9][0-9]{5}$/;
                  if (pinRegex.test(val)) {
                    setFormErrors(prev => ({ ...prev, pincode: '' }));
                  }
                }
              }}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
            />
            {formErrors.pincode && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.pincode}</p>
            )}
          </div>

          {/* Dynamic options based on type */}
          {activeSubTab === 'subscriber' ? (
            <div>
              <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                Manually Charged Price (₹ INR) *
              </label>
              <input
                type="number"
                min={0}
                value={form.amount}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setForm(f => ({ ...f, amount: val }));
                  if (!isNaN(val) && val >= 0) {
                    setFormErrors(prev => ({ ...prev, amount: '' }));
                  }
                }}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
              />
              {formErrors.amount && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5">{formErrors.amount}</p>
            )}
          </div>
          ) : (
            <div className="flex items-center gap-3.5 pt-6 select-none">
              <input
                type="checkbox"
                id="give_1_year"
                checked={form.give_1_year}
                onChange={(e) => setForm(f => ({ ...f, give_1_year: e.target.checked }))}
                className="w-4.5 h-4.5 rounded border-gray-300 text-[#2ab391] focus:ring-[#2ab391] cursor-pointer"
              />
              <label htmlFor="give_1_year" className="text-xs font-bold text-gray-600 cursor-pointer">
                Give Only 1 Year Subscription (otherwise, provide Lifetime/Unlimited)
              </label>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pt-3">
          <button
            type="button"
            onClick={handleResetForm}
            className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
          >
            Clear Fields
          </button>
          <button
            type="submit"
            disabled={submittingAdd}
            className="px-5 py-2.5 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-xl text-xs font-bold shadow-md shadow-[#2ab391]/10 flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-60"
          >
            {submittingAdd ? <Loader size={14} className="animate-spin" /> : null}
            <span>{submittingAdd ? 'Granting...' : 'Grant Subscription'}</span>
          </button>
        </div>
      </form>

      {/* Manual Grants History List */}
      <div className="space-y-4 text-left">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h4 className="text-sm font-bold text-gray-800">Manually Granted Registry Logs</h4>
            <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Audit log of all admin-issued subscriptions and guest passes.</p>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 p-2 rounded-xl">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date:</span>
            <DateRangePicker
              startDate={tableFilters.start_date}
              endDate={tableFilters.end_date}
              onChange={(start, end) => {
                setTableFilters(f => ({ ...f, start_date: start, end_date: end }));
              }}
              placeholder="Select Date Range"
            />
            {(tableFilters.start_date || tableFilters.end_date) && (
              <button
                onClick={() => setTableFilters({ start_date: '', end_date: '' })}
                className="px-2.5 py-1 text-xs font-semibold text-red-650 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {loadingHistory ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 bg-white rounded-2xl border border-gray-150">
            <RefreshCw className="animate-spin text-[#2ab391] w-6 h-6" />
            <span className="text-xs font-bold text-gray-400">Loading audit trail...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 bg-white rounded-2xl border border-gray-150">
            <Inbox size={26} className="text-gray-300" />
            <p className="text-xs text-gray-400 font-bold">No manual subscriptions granted matching this filter.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-150 overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-150">
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">User Email</th>
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Price Charged</th>
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Validity Period</th>
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Issued By</th>
                      <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-semibold text-xs text-gray-700">
                    {history.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/20 transition-colors">
                        <td className="px-5 py-3 text-gray-800 font-bold">{item.user_email}</td>
                        <td className="px-5 py-3 text-emerald-600 font-bold">₹{item.amount}</td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(item.start_date)} — {item.end_date ? formatDate(item.end_date) : 'Lifetime'}
                        </td>
                        <td className="px-5 py-3">
                          {item.subscription_type === 'GUEST' ? (
                            <span className="whitespace-nowrap inline-block px-2.5 py-1 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 text-[10px] font-bold">
                              Guest Pass
                            </span>
                          ) : (
                            <span className="whitespace-nowrap inline-block px-2.5 py-1 rounded bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] font-bold">
                              Paid Sub
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-500 font-mono text-[10px]">{item.admin_email || 'System'}</td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => handleOpenEdit(item)}
                            className="p-1 rounded bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 cursor-pointer inline-flex items-center gap-1.5"
                          >
                            <Edit2 size={11} />
                            <span>Edit</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Manual List Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs font-bold text-gray-500">
                <span>Page {page} of {totalPages} ({totalItems} records)</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1 rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1 rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Entry Modal Dialog */}
      {editItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-sm font-bold text-gray-800">Edit Manual Subscription</h3>
                <p className="text-[10px] text-gray-400 mt-0.5">Updating: {editItem.user_email}</p>
              </div>
              <button
                onClick={() => setEditItem(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4 font-semibold text-xs">
              {/* Delivery Address */}
              <div>
                <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Delivery / Courier Address *
                </label>
                <input
                  type="text"
                  value={editForm.delivery_address}
                  onChange={(e) => setEditForm(f => ({ ...f, delivery_address: e.target.value }))}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                  required
                />
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Pincode *
                </label>
                <input
                  type="text"
                  value={editForm.pincode}
                  onChange={(e) => setEditForm(f => ({ ...f, pincode: e.target.value }))}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                  required
                />
              </div>

              {/* Price (if Subscriber) */}
              {editItem.subscription_type === 'SUBSCRIBER' && (
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                    Amount Charged (₹ INR) *
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.amount}
                    onChange={(e) => setEditForm(f => ({ ...f, amount: Number(e.target.value) }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                    required
                  />
                </div>
              )}

              {/* Validity Expiry (if not Lifetime Guest) */}
              {editItem.subscription_type === 'GUEST' && (
                <div className="flex items-center gap-2 select-none mb-2">
                  <input
                    type="checkbox"
                    id="edit_is_lifetime"
                    checked={editForm.is_lifetime}
                    onChange={(e) => setEditForm(f => ({ ...f, is_lifetime: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-[#2ab391] focus:ring-[#2ab391] cursor-pointer"
                  />
                  <label htmlFor="edit_is_lifetime" className="text-xs font-bold text-gray-600 cursor-pointer">
                    Lifetime Validity (No Expiry Date)
                  </label>
                </div>
              )}

              {!editForm.is_lifetime && (
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                    Expiry Expiration Date *
                  </label>
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                    required
                  />
                </div>
              )}
            </div>

            <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditItem(null)}
                className="px-3.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 font-bold hover:text-gray-700 cursor-pointer text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={submittingEdit}
                className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-colors disabled:opacity-60"
              >
                {submittingEdit ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                <span>{submittingEdit ? 'Saving...' : 'Update Entry'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagazineManual;
